package io.jsonize.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import io.jsonize.web.domain.model.HistoryItem
import io.jsonize.web.viewmodel.AppViewModel
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import kotlin.time.ExperimentalTime

private const val OVERLAY_ID = "history-overlay"
private const val LIST_ID = "history-list"
private const val SCRIM_ID = "history-scrim"
private const val CLOSE_ID = "history-close"
private const val CLEAR_ID = "history-clear"

private const val DELETE_ICON_SVG =
    "<svg viewBox=\"0 0 24 24\" width=\"18\" height=\"18\" aria-hidden=\"true\" focusable=\"false\">" +
        "<path fill=\"currentColor\" d=\"M6 7h12l-1 14H7L6 7zm3-3h6l1 2H8l1-2z\"/>" +
    "</svg>"

private var onOverlayDismiss: (() -> Unit)? = null

fun showHistoryOverlay() {
    overlayRoot()?.let { root ->
        root.classList.add("is-visible")
        root.setAttribute("aria-hidden", "false")
    }
}

private fun hideHistoryOverlay() {
    overlayRoot()?.let { root ->
        root.classList.remove("is-visible")
        root.setAttribute("aria-hidden", "true")
    }
    onOverlayDismiss?.invoke()
}

@Composable
fun HtmlHistoryOverlayBridge(
    viewModel: AppViewModel,
    onDismiss: (() -> Unit)? = null
) {
    val history by viewModel.history.collectAsState()

    LaunchedEffect(history) {
        renderHistoryList(history, viewModel)
    }

    DisposableEffect(onDismiss) {
        onOverlayDismiss = onDismiss
        val scrim = document.getElementById(SCRIM_ID) as? HTMLElement
        val closeButton = document.getElementById(CLOSE_ID) as? HTMLElement
        val clearButton = document.getElementById(CLEAR_ID) as? HTMLElement

        val closeListener = EventListener { hideHistoryOverlay() }
        val clearListener = EventListener {
            if (window.confirm("Clear all history?")) {
                viewModel.clearHistory()
            }
        }

        scrim?.addEventListener("click", closeListener)
        closeButton?.addEventListener("click", closeListener)
        clearButton?.addEventListener("click", clearListener)

        onDispose {
            if (onOverlayDismiss == onDismiss) {
                onOverlayDismiss = null
            }
            scrim?.removeEventListener("click", closeListener)
            closeButton?.removeEventListener("click", closeListener)
            clearButton?.removeEventListener("click", clearListener)
        }
    }
}

private fun overlayRoot(): HTMLElement? =
    document.getElementById(OVERLAY_ID) as? HTMLElement

private fun renderHistoryList(history: List<HistoryItem>, viewModel: AppViewModel) {
    val list = document.getElementById(LIST_ID) as? HTMLElement ?: return
    list.innerHTML = ""

    if (history.isEmpty()) {
        val empty = document.createElement("div") as HTMLElement
        empty.className = "history-empty"
        empty.textContent = "No history yet"
        list.appendChild(empty)
        return
    }

    history.forEach { item ->
        val row = document.createElement("div") as HTMLElement
        row.className = "history-row"
        row.onclick = {
            viewModel.applyHistoryItem(item)
            hideHistoryOverlay()
            null
        }

        val content = document.createElement("div") as HTMLElement
        content.className = "history-row-content"

        val title = document.createElement("div") as HTMLElement
        title.className = "history-title"
        title.textContent = truncateTitle(item.title)

        val meta = document.createElement("div") as HTMLElement
        meta.className = "history-meta"
        meta.textContent = formatTimestamp(item.timestamp)

        content.appendChild(title)
        content.appendChild(meta)

        val deleteButton = document.createElement("button") as HTMLButtonElement
        deleteButton.className = "icon-btn danger"
        deleteButton.setAttribute("title", "Delete history item")
        deleteButton.setAttribute("aria-label", "Delete history item")
        deleteButton.innerHTML = DELETE_ICON_SVG
        deleteButton.onclick = { event ->
            event?.stopPropagation()
            viewModel.deleteHistoryItem(item.id)
            null
        }

        row.appendChild(content)
        row.appendChild(deleteButton)
        list.appendChild(row)

        val divider = document.createElement("div") as HTMLElement
        divider.className = "history-divider"
        list.appendChild(divider)
    }
}

@OptIn(ExperimentalTime::class)
private fun formatTimestamp(instant: kotlinx.datetime.Instant): String {
    val dt = instant.toLocalDateTime(TimeZone.currentSystemDefault())
    val date = "${dt.year}-${twoDigits(dt.monthNumber)}-${twoDigits(dt.dayOfMonth)}"
    val time = "${twoDigits(dt.hour)}:${twoDigits(dt.minute)}"
    return "$date $time"
}

private fun twoDigits(value: Int): String =
    value.toString().padStart(2, '0')

private fun truncateTitle(title: String, max: Int = 48): String {
    if (title.length <= max) return title
    return title.take(max - 3) + "..."
}
